import type { ReactNode } from "react";
import { useState } from "react";
import { toThumbnailUrl } from "@/utils/image";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger
} from "@/components/ui/hover-card";

export interface ProductThumbnailImageProps {
  src: string | null;
  alt: string;
  className?: string;
  /** Rendered instead of the `<img>` once both the thumbnail and the full-size original have failed to load. */
  fallback: ReactNode;
  /**
   * When `true` (the default), hovering the thumbnail opens a floating preview
   * of the full-size original image, capped at 800px (and the viewport). Set
   * to `false` where a preview would be redundant or intrusive (e.g. an
   * already-large header image).
   */
  enablePreview?: boolean;
}

type ThumbnailImgProps = ProductThumbnailImageProps & { src: string };

/**
 * Requests the CDN's pre-generated 160x160 thumbnail instead of the
 * full-size original (often several hundred KB) for a product image. Falls
 * back to the original URL if the thumbnail 404s (e.g. an image ingested
 * before thumbnails were generated), then to `fallback` if that fails too.
 * `toThumbnailUrl` no-ops for non-salonstocker-CDN URLs (e.g. supplier
 * images), so this is always safe to use.
 *
 * On hover (unless `enablePreview` is `false`) it also reveals the full-size
 * original in a floating card, capped at 800px, so buyers can inspect a
 * product without leaving the list. The preview `<img>` only mounts while the
 * card is open, so the full-size original is never fetched just to sit behind
 * a thumbnail nobody hovers.
 *
 * Keyed by `src` from the wrapping component below so the thumb/original/
 * broken attempt state resets whenever the image identity changes, even if
 * the parent doesn't remount (e.g. a memoized card whose thumbnail prop
 * updates in place).
 */
function ThumbnailImg({
  src,
  alt,
  className,
  fallback,
  enablePreview = true
}: ThumbnailImgProps) {
  const thumbSrc = toThumbnailUrl(src);
  const hasDistinctThumb = thumbSrc !== src;
  const [attempt, setAttempt] = useState<"thumb" | "original" | "broken">(
    hasDistinctThumb ? "thumb" : "original"
  );
  const [previewBroken, setPreviewBroken] = useState(false);

  if (attempt === "broken") return <>{fallback}</>;

  const img = (
    <img
      src={attempt === "thumb" ? thumbSrc : src}
      alt={alt}
      className={className}
      loading="lazy"
      onError={() =>
        setAttempt((cur) => (cur === "thumb" ? "original" : "broken"))
      }
    />
  );

  // Skip the preview when disabled, or once the full-size original has itself
  // failed to load - so hovering never opens an empty card.
  if (!enablePreview || previewBroken) return img;

  return (
    <HoverCard openDelay={200} closeDelay={100}>
      <HoverCardTrigger asChild>{img}</HoverCardTrigger>
      <HoverCardContent
        side="right"
        sideOffset={12}
        collisionPadding={16}
        className="border-border pointer-events-none bg-white p-1 shadow-xl dark:border-neutral-700 dark:bg-neutral-900"
      >
        <img
          src={src}
          alt={alt}
          onError={() => setPreviewBroken(true)}
          style={{
            maxWidth: "min(800px, 80vw)",
            maxHeight: "min(800px, 80vh)"
          }}
          className="block h-auto w-auto rounded-md object-contain"
        />
      </HoverCardContent>
    </HoverCard>
  );
}

export function ProductThumbnailImage({
  src,
  alt,
  className,
  fallback,
  enablePreview
}: ProductThumbnailImageProps) {
  if (!src) return <>{fallback}</>;
  return (
    <ThumbnailImg
      key={src}
      src={src}
      alt={alt}
      className={className}
      fallback={fallback}
      enablePreview={enablePreview}
    />
  );
}
